import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { ImportSourceType, fetchPlannerTasks } from '../../../services/importService';
import { Project } from '../../../types';
import { listProjects, createProject } from '../../../services/projectService';
import { ImportTaskMeta } from '../../../services/importConnectors';

interface Props {
  pca: IPublicClientApplication;
  source: number;
  connectionData: {
    headers?: string[]; rows?: unknown[][]; planId?: string; sourceLabel?: string;
    hierarchy?: { collections: string[]; taskMeta?: ImportTaskMeta[]; dependencyCount: number };
  } | null;
  mapping: Record<string, string>;
  targetProjectId: string | null;
  onSetProject: (id: string) => void;
  onBeginImport: () => void;
}

export default function PreviewStep({ pca, source, connectionData, mapping: _mapping, targetProjectId, onSetProject, onBeginImport }: Props) {
  const [plannerRows, setPlannerRows] = useState<{ id: string; title: string; dueDateTime?: string | null; percentComplete?: number }[]>([]);
  const [loading, setLoading]     = useState(false);
  const [showConstraints, setShowConstraints] = useState(false);
  const [projects, setProjects]   = useState<Project[]>([]);
  const [newName, setNewName]     = useState('');
  const [creating, setCreating]   = useState(false);

  useEffect(() => {
    listProjects(pca).then(setProjects).catch(() => {});
  }, [pca]);

  useEffect(() => {
    if (source === ImportSourceType.Planner && connectionData?.planId) {
      setLoading(true);
      fetchPlannerTasks(pca, connectionData.planId)
        .then(setPlannerRows)
        .finally(() => setLoading(false));
    }
  }, [pca, source, connectionData]);

  const handleCreateProject = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const id = await createProject(pca, newName.trim());
      setProjects([...projects, { dagrun_pm_projectid: id, dagrun_pm_name: newName.trim() } as Project]);
      onSetProject(id);
      setNewName('');
    } finally { setCreating(false); }
  };

  const headers = connectionData?.headers ?? [];
  const rows    = connectionData?.rows ?? [];
  const label   = connectionData?.sourceLabel ?? '';
  const hierarchy = connectionData?.hierarchy;

  // Tasks carrying a scheduling constraint (MPP XML) — drives the
  // acknowledgement banner and the review list.
  const constrained = (hierarchy?.taskMeta ?? [])
    .map((m, i) => ({ meta: m, name: String(rows[i]?.[0] ?? `Row ${i + 1}`) }))
    .filter(x => x.meta.constraint);

  return (
    <div>
      <h3>Preview{label ? ` — ${label}` : ''}</h3>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>Import into project</label>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <select value={targetProjectId ?? ''} onChange={e => onSetProject(e.target.value)} style={{ flex: 1 }}>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p.dagrun_pm_projectid} value={p.dagrun_pm_projectid}>{p.dagrun_pm_name}</option>)}
          </select>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New project name" />
          <button className="btn-secondary" onClick={handleCreateProject} disabled={creating || !newName.trim()}>Create</button>
        </div>
      </div>

      {loading && <div>Loading preview...</div>}

      {source === ImportSourceType.Planner && plannerRows.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>{plannerRows.length} tasks found</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: 6 }}>Title</th><th>Due</th><th>% Complete</th>
            </tr></thead>
            <tbody>
              {plannerRows.slice(0, 20).map(r => (
                <tr key={r.id} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                  <td style={{ padding: 6 }}>{r.title}</td>
                  <td>{r.dueDateTime ? new Date(r.dueDateTime).toLocaleDateString() : '—'}</td>
                  <td>{r.percentComplete ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* WBS hierarchy summary (Microsoft Project XML / Project Online) */}
      {hierarchy && hierarchy.collections.length > 0 && (
        <div style={{ marginBottom: 16, padding: 12, background: 'var(--surface-2)', borderRadius: 'var(--r-sm)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
            Collections to be created ({hierarchy.collections.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {hierarchy.collections.map((c, i) => (
              <span key={i} className="pill" style={{ fontSize: 11 }}>{c}</span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {hierarchy.dependencyCount} predecessor link{hierarchy.dependencyCount === 1 ? '' : 's'} will be imported as dependencies
          </div>
        </div>
      )}

      {/* Scheduling-constraint acknowledgement (MPP XML) */}
      {constrained.length > 0 && (
        <div style={{
          marginBottom: 16, padding: 12,
          background: 'var(--brand-mist)', borderRadius: 'var(--r-sm)',
          border: '1px solid var(--brand)',
        }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            <strong style={{ color: 'var(--brand)' }}>
              {constrained.length} task{constrained.length === 1 ? ' has' : 's have'} scheduling constraints that will be preserved.
            </strong>{' '}
            <button
              type="button"
              onClick={() => setShowConstraints(s => !s)}
              style={{ background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', padding: 0, textDecoration: 'underline' }}
            >{showConstraints ? 'Hide constraints' : 'Review constraints'}</button>
          </div>
          {showConstraints && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 }}>
              <thead><tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <th style={{ padding: 6 }}>Task</th>
                <th style={{ padding: 6 }}>Constraint</th>
                <th style={{ padding: 6 }}>Date</th>
              </tr></thead>
              <tbody>
                {constrained.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                    <td style={{ padding: 6 }}>{c.name}</td>
                    <td style={{ padding: 6 }}>{c.meta.constraint!.typeLabel}</td>
                    <td style={{ padding: 6, fontFamily: 'var(--font-mono)' }}>
                      {c.meta.constraint!.date ? new Date(c.meta.constraint!.date).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Rowset preview covers Excel/CSV, Project XML, Monday.com, Smartsheet */}
      {source !== ImportSourceType.Planner && headers.length > 0 && rows.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>
            {rows.length} row{rows.length === 1 ? '' : 's'} found — showing first 20
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: '100%' }}>
              <thead><tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                {headers.map(h => <th key={h} style={{ padding: 6, whiteSpace: 'nowrap' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.slice(0, 20).map((row, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid var(--border-light)' }}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ padding: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                        {cell == null ? '' : String(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {source !== ImportSourceType.Planner && rows.length === 0 && (
        <div style={{ color: 'var(--ink-4)' }}>No rows to preview. Go back and re-connect.</div>
      )}

      <div style={{ marginTop: 20 }}>
        <button className="btn-primary" onClick={onBeginImport} disabled={!targetProjectId}>
          Begin Import
        </button>
        {!targetProjectId && <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--danger)' }}>Select a target project first</span>}
      </div>
    </div>
  );
}
